import { LaunchRounded } from "@mui/icons-material";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, SvgIcon } from "@mui/material";
import { alpha, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { keyframes } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import { getClickableLinkPath, getCompletedLinkPath } from "../../assets/svgGraphics";
import OmniAvatar from "../ui/omniAvatar";

const muiSvgIconHover = keyframes`
from {transform: scale(1) rotate(15deg);}
33% {transform: scale(1.10) rotate(17.5deg);}
66% {transform: scale(1) rotate(12.5deg);}
to {transform: rotate(15deg);}
`;

const EmpireToDoListItem = props => {
  const { toDoODM, onClickHandler = null } = props;
  const theme = useTheme();

  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));

  const getBusinessPillarColor = businessPillarEnum => {
    switch (businessPillarEnum) {
      case constants.empirePillarEnumes.leadership:
        return theme.palette.highlight.main;
      case constants.empirePillarEnumes.opertations:
        return theme.palette.error.main;
      case constants.empirePillarEnumes.marketing:
        return theme.palette.warning.main;
      case constants.empirePillarEnumes.finance:
        return theme.palette.success.main;
      case constants.empirePillarEnumes.personnel:
        return theme.palette.info.main;
      default:
        return theme.palette.secondary.main;
    }
  };

  const getToDoMainColor = toDo => {
    if (toDo.isCompleted) {
      return "#3d9461";
    }

    return getBusinessPillarColor(toDo.businessPillar);
  };

  const getSvgIconPath = toDo => {
    return toDo.isCompleted ? getCompletedLinkPath() : getClickableLinkPath();
  };

  const getEditButtonToolTip = (toDoType, isCompleted = false) => {
    return isCompleted
      ? "Nice Work! Completed!"
      : toDoType === "profileCompletion"
        ? "Open User Dialog"
        : toDoType === "preScreenWorksheet"
          ? "Launch Memory Questionnaire Wizard"
          : toDoType === "openInvitation"
            ? "Open Invitation Dialog"
            : "";
  };

  const getEditButtonIcon = (type, isCompleted) => {
    if (isCompleted) {
      return <CheckBoxRoundedIcon sx={{ height: 24, width: 24 }} />;
    }

    if (type === "medication") {
      return <CheckBoxOutlineBlankRoundedIcon sx={{ height: 24, width: 24 }} />;
    }

    if (type === "profile") {
      return <EditIcon sx={{ height: 24, width: 24 }} />;
    }

    if (type === "assessment") {
      return <LaunchRounded sx={{ height: 24, width: 24 }} />;
    }
    return <LaunchRounded sx={{ height: 24, width: 24 }} />;
  };

  return (
    <ListItem
      key={toDoODM.key}
      disableGutters={true}
      sx={[
        {
          paddingRight: theme.spacing(1),
          paddingLeft: theme.spacing(1),
          paddingTop: 0,
          paddingBottom: 0,
          marginBottom: theme.spacing(1),
          borderRadius: theme.spacing(1),
          boxShadow: theme.shadows[3],
          cursor: "pointer",
          userSelect: "none",
          webkitUserSelect: "none",
          color: theme.palette.info.contrastText,
          backgroundColor: alpha(getToDoMainColor(toDoODM), 0.75),
          "& .MuiIconButton-root": {
            transition: "all 0.15s ease-out",
            opacity: 0,
          },
          "&:hover": {
            backgroundColor: alpha(getToDoMainColor(toDoODM), 0.5),
            cursor: "pointer",
            "& .MuiIconButton-root": {
              opacity: 1,
            },
            "& .MuiAvatar-root": {
              "& .MuiSvgIcon-root": {
                animationName: `${muiSvgIconHover}`,
                animationDuration: ".3s",
                animationIterationCount: "1",
                animationTimingFunction: "ease-in-out",
                "& path": {
                  d: `path("M8.79 9.24V5.5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v3.74c1.21-.81 2-2.18 2-3.74 0-2.49-2.01-4.5-4.5-4.5s-4.5 2.01-4.5 4.5c0 1.56.79 2.93 2 3.74zm5.5 2.47c-.28-.14-.58-.21-.89-.21h-.61v-6c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v10.74l-3.44-.72c-.37-.08-.76.04-1.03.31-.43.44-.43 1.14 0 1.58l4.01 4.01c.38.37.89.58 1.42.58h6.1c1 0 1.84-.73 1.98-1.72l.63-4.47c.12-.85-.32-1.69-1.09-2.07l-4.08-2.03z")`,
                },
              },
            },
          },
        },
      ]}
      onClick={() => onClickHandler(toDoODM)}
    >
      <Tooltip placement="right">
        <OmniAvatar
          sx={[
            {
              width: 36,
              height: 36,
              fontSize: "1.8rem",
              backgroundColor: getToDoMainColor(toDoODM),
              border: `2px solid ${theme.palette.info.contrastText}`,
            },
          ]}
        >
          <SvgIcon
            sx={[
              {
                height: 24,
                width: 24,
                color: theme.palette.info.contrastText,
                backgroundColor: getToDoMainColor(toDoODM),
                transform: `rotate(15deg)`,
              },
            ]}
          >
            {getSvgIconPath(toDoODM)}
          </SvgIcon>
        </OmniAvatar>
      </Tooltip>
      <ListItemText
        primary={
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: "-2px !Important",
              marginLeft: theme.spacing(1),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: theme.palette.info.contrastText,
              webkitUserSelect: "none",
              userSelect: "none",
            }}
          >
            {toDoODM.description}
          </Typography>
        }
        secondary={
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              flexDirection: "row",
              marginLeft: theme.spacing(1),
              marginTop: `-${theme.spacing(0.75)}`,
              marginBottom: `-${theme.spacing(0.9)}`,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: theme.palette.info.contrastText,
              webkitUserSelect: "none",
              userSelect: "none",
              textTransform: "capitalize",
            }}
          >
            {`${toDoODM.isCompleted ? toDoODM.completedText : toDoODM.incompleteText}`}
          </Typography>
        }
      />
      {!mobileLayout && (
        <Tooltip
          title={getEditButtonToolTip(toDoODM.toDoType, toDoODM.isCompleted)}
          placement="left"
        >
          <IconButton
            sx={[
              {
                position: "absolute",
                top: theme.spacing(1),
                right: theme.spacing(1),
                height: 32,
                width: 32,
                borderRadius: "50%",
                "&:hover": {
                  color: theme.palette.info.contrastText,
                  background: getToDoMainColor(toDoODM),
                },
              },
            ]}
            onClick={() => onClickHandler(toDoODM)}
            size="large"
          >
            {getEditButtonIcon(toDoODM.type, toDoODM.isCompleted)}
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
};

export default EmpireToDoListItem;
